import * as THREE from 'three';

export interface Mission {
  id: string;
  title: string;
  category: 'Grundfahraufgabe' | 'StVO-Training' | 'Prüfungssimulation' | 'Freies Fahren';
  description: string;
  instructions: string[];
  startPosition: THREE.Vector3;
  startRotationY: number;
  speedLimit: number;
  timeLimitSeconds: number;
  requiredObjectives: string[];
}

export const MISSIONS: Mission[] = [
  {
    id: 'vollpruefung',
    title: 'Komplette Praktische Fahrprüfung (TÜV/DEKRA)',
    category: 'Prüfungssimulation',
    description: 'Umfassende Fahrprüfungssimulation mit allen StVO-Regeln, Stadtfahrt, Ampeln, Kreisverkehr und Prüfungsaufgaben.',
    instructions: [
      'Folgen Sie den Anweisungen des Prüfers.',
      'Beachten Sie Geschwindigkeitsbegrenzungen (30er-Zonen, 50er-Hauptstraßen).',
      'Achten Sie auf Stoppschilder (3 Sek. Stillstand), Zebrastreifen, Ampeln und den Kreisverkehr.',
      'Führen Sie die vom Prüfer aufgetragene Parkaufgabe und Gefahrenbremsung fehlerfrei aus.'
    ],
    startPosition: new THREE.Vector3(-2.0, 0.5, 24.0), // Right lane, heading north (rotY=0)
    startRotationY: 0,
    speedLimit: 50,
    timeLimitSeconds: 300,
    requiredObjectives: [
      'Alle Prüferanweisungen befolgt',
      'Keine schwerwiegenden StVO-Verstöße',
      'Mindestpunktzahl ≥ 75%'
    ]
  },
  {
    id: 'laengsparken',
    title: 'Grundfahraufgabe: Längsparken (Parallel)',
    category: 'Grundfahraufgabe',
    description: 'Rückwärts in eine Parklücke zwischen zwei Fahrzeugen parallel zum Bordstein einparken.',
    instructions: [
      'Fahren Sie parallel neben das vordere Fahrzeug.',
      'Setzen Sie den rechten Blinker und sichern Sie den Verkehr mit Innenspiegel, Außenspiegel und Schulterblick ab.',
      'Lenken Sie zügig nach rechts ein und fahren Sie rückwärts in die Lücke.',
      'Gegenlenken, um parallel zum Bordstein zu stehen (Abstand ≤ 30 cm, max. 2 Korrekturzüge).',
      'Der Bordstein darf nicht überfahren oder hart gerammt werden!'
    ],
    startPosition: new THREE.Vector3(49.1, 0.5, -28.8), // On East cross street (x=50), heading south (rotY=π) alongside parallel bay
    startRotationY: Math.PI,
    speedLimit: 15,
    timeLimitSeconds: 120,
    requiredObjectives: [
      'Rechter Blinker vor Rückwärtsfahrt',
      'Schulterblick rechts abgesichert',
      'Bordsteinabstand ≤ 30 cm',
      'Kein Bordsteinaufprall'
    ]
  },
  {
    id: 'querparken',
    title: 'Grundfahraufgabe: Querparken (Rückwärts)',
    category: 'Grundfahraufgabe',
    description: 'Rückwärts in eine rechtwinklige Parkbucht zwischen Markierungslinien einfahren.',
    instructions: [
      'Blinken Sie rechts und halten Sie vor der Parkbucht an.',
      'Sichern Sie nach allen Seiten mit dem 3-S-Blick ab.',
      'Rückwärts mittig zwischen den weißen Begrenzungslinien einparken.'
    ],
    startPosition: new THREE.Vector3(21.0, 0.5, -60.5), // On South residential street (z=-60), heading east (rotY=π/2) past bayParkingCenter (25, 0, -66)
    startRotationY: Math.PI / 2,
    speedLimit: 15,
    timeLimitSeconds: 90,
    requiredObjectives: ['Mittig in Parkbucht', 'Rückraum abgesichert', 'Stillstand erreicht']
  },
  {
    id: 'umkehren_wenden',
    title: 'Grundfahraufgabe: Umkehren / Wenden (3-Punkt)',
    category: 'Grundfahraufgabe',
    description: 'Wenden auf enger Fahrbahn in maximal 3 Zügen unter Rückwärtsfahren und vollständiger Verkehrsbeobachtung.',
    instructions: [
      'Halten Sie am rechten Fahrbahnrand an und setzen Sie den linken Blinker.',
      'Sichern Sie mit Innenspiegel, Außenspiegel und Schulterblick nach vorne und hinten ab.',
      'Lenken Sie voll nach links ein und fahren Sie bis kurz vor den linken Bordstein.',
      'Schalten Sie in den Rückwärtsgang, sichern Sie den Rückraum (360°-Umschau) ab und setzen Sie zurück.',
      'Fahren Sie im 3. Zug in entgegengesetzter Fahrtrichtung weiter, ohne den Bordstein zu berühren!'
    ],
    startPosition: new THREE.Vector3(-48.8, 0.5, -26.0), // On West cross street (x=-50) right lane, heading south (rotY=π) into Wenden zone (-50, 0, -35)
    startRotationY: Math.PI,
    speedLimit: 15,
    timeLimitSeconds: 120,
    requiredObjectives: [
      'Blinker & Schulterblick vor dem Anfahren',
      'Rückraum bei Rückwärtsfahrt abgesichert',
      'Fahrtrichtung um 180° gedreht (max. 3 Züge)',
      'Kein Bordsteinkontakt'
    ]
  },
  {
    id: 'gefahrenbremsung',
    title: 'Grundfahraufgabe: Gefahrenbremsung',
    category: 'Grundfahraufgabe',
    description: 'Abbremsen mit höchstmöglicher Verzögerung aus 30 km/h ohne die Spur zu verlassen.',
    instructions: [
      'Beschleunigen Sie gleichmäßig auf genau 30 km/h.',
      'Warten Sie auf das akustische Prüfer-Kommando: "Gefahrenbremsung! JETZT!"',
      'Treten Sie schlagartig mit maximaler Kraft auf das Bremspedal (und Kupplung bei Schaltwagen).',
      'Halten Sie das Lenkrad gerade fest, bis das Fahrzeug komplett steht.'
    ],
    startPosition: new THREE.Vector3(-2.0, 0.5, 24.0), // Right lane of main boulevard, heading north (rotY=0)
    startRotationY: 0,
    speedLimit: 50,
    timeLimitSeconds: 45,
    requiredObjectives: ['Auf 30 km/h beschleunigt', 'Verzögerung > 6.5 m/s²', 'Spur gehalten']
  },
  {
    id: 'kreisverkehr',
    title: 'Kreisverkehr Meistern (§ 8 Abs. 1a StVO)',
    category: 'StVO-Training',
    description: 'Korrekte Vorfahrts- und Blinkregeln im deutschen Kreisverkehr anwenden.',
    instructions: [
      'Heranfahren an den Kreisverkehr, Vorfahrt des kreisenden Verkehrs beachten.',
      'ACHTUNG: Bei der Einfahrt in den Kreisverkehr darf NICHT geblinkt werden!',
      'Fahren Sie in den Kreisverkehr ein und nehmen Sie die 3. Ausfahrt.',
      'Rechtzeitig nach Passieren der 2. Ausfahrt den rechten Blinker setzen.',
      'Schulterblick nach rechts auf den Rad- und Fußweg vor dem Ausfahren durchführen!'
    ],
    startPosition: new THREE.Vector3(2.0, 0.5, 55), // Right lane, heading south (rotY=π)
    startRotationY: Math.PI,
    speedLimit: 30,
    timeLimitSeconds: 90,
    requiredObjectives: [
      'Kein Blinker bei Einfahrt',
      'Vorfahrt im Kreisel beachtet',
      'Rechter Blinker vor Ausfahrt',
      'Schulterblick Radweg'
    ]
  },
  {
    id: 'rechts_vor_links',
    title: 'Rechts-vor-Links im Wohngebiet (§ 8 StVO)',
    category: 'StVO-Training',
    description: 'Vorfahrtsregelung an unbeschilderten Einmündungen in der Tempo-30-Zone.',
    instructions: [
      'Fahren Sie mit maximal 30 km/h durch das Wohngebiet.',
      'An unbeschilderten Kreuzungen bremsbereit heranfahren und nach rechts sichern.',
      'Fahrzeugen von rechts zwingend die Vorfahrt gewähren!'
    ],
    startPosition: new THREE.Vector3(-2.0, 0.5, 24.0), // Right lane, heading north (rotY=0), safe distance north of roundabout
    startRotationY: 0,
    speedLimit: 30,
    timeLimitSeconds: 120,
    requiredObjectives: ['Bremsbereitschaft an Kreuzungen', 'Rechts-vor-Links gewährt', 'Tempo 30 eingehalten']
  },
  {
    id: 'freies_fahren',
    title: 'Freies Fahren & Üben',
    category: 'Freies Fahren',
    description: 'Freie Erkundung der Stadt mit Live-StVO-Überwachung und Fahrfehleranzeige.',
    instructions: ['Fahren Sie frei durch die Straßen.', 'Üben Sie nach eigenem Tempo das Handling und Parken.'],
    startPosition: new THREE.Vector3(-2.0, 0.5, 24.0), // Right lane, heading north (rotY=0)
    startRotationY: 0,
    speedLimit: 50,
    timeLimitSeconds: 9999,
    requiredObjectives: ['Freies Fahren']
  },
  {
    id: 'sonderfahrt-autobahn',
    title: 'Sonderfahrt: Autobahn',
    category: 'StVO-Training',
    description: 'Pflichtfahrstunde Autobahn: Auffahren über Beschleunigungsstreifen, Richtgeschwindigkeit 130 km/h, Rechtsfahrgebot beachten und Ausfahren.',
    instructions: [
      'Fahren Sie über den Beschleunigungsstreifen auf die Autobahn ein.',
      'Beschleunigen Sie zügig und fädeln Sie sicher ein.',
      'Halten Sie sich an das Rechtsfahrgebot (§ 2 StVO) und die Richtgeschwindigkeit 130 km/h.',
      'Verlassen Sie die Autobahn an der nächsten Ausfahrt.'
    ],
    startPosition: new THREE.Vector3(4, 0.5, -480),
    startRotationY: 0,
    speedLimit: 130,
    timeLimitSeconds: 240,
    requiredObjectives: [
      'Auf die Autobahn auffahren (Beschleunigungsstreifen nutzen)',
      'Richtgeschwindigkeit 130 km/h fahren',
      'Rechtsfahrgebot einhalten (§ 2 Abs. 2 StVO)',
      'Überholverbot rechts beachten (§ 5 StVO)',
      'Autobahn über Ausfahrt verlassen',
    ]
  },
  {
    id: 'sonderfahrt-ueberland',
    title: 'Sonderfahrt: Überlandfahrt',
    category: 'StVO-Training',
    description: 'Überlandfahrt auf der Landstraße: Tempo 100 km/h außerhalb geschlossener Ortschaften, Ortstafeln beachten und vorausschauend fahren.',
    instructions: [
      'Verlassen Sie die geschlossene Ortschaft.',
      'Beachten Sie das Ende der 50 km/h-Zone an der Ortstafel.',
      'Beschleunigen Sie auf der Landstraße auf bis zu 100 km/h.',
      'Bremsen Sie rechtzeitig vor Kurven und Einmündungen ab.'
    ],
    startPosition: new THREE.Vector3(2, 0.5, 180),
    startRotationY: 0,
    speedLimit: 100,
    timeLimitSeconds: 200,
    requiredObjectives: [
      'Ortsausgang passieren (Zeichen 311 Ortstafel)',
      'Auf Landstraßengeschwindigkeit beschleunigen (bis 100 km/h)',
      'Abstand einhalten und Kurven anbremsen',
      'Rechtsfahrgebot einhalten',
    ]
  },
  {
    id: 'engstelle-parkende-autos',
    title: 'Engstelle: Parkende Fahrzeuge',
    category: 'StVO-Training',
    description: 'Vorbeifahren an parkenden Fahrzeugen (§ 6 StVO): Gegenverkehr beachten, rechtzeitig nach links blinken und ausreichenden Seitenabstand halten.',
    instructions: [
      'Fahren Sie vorsichtig in die verengte Wohnstraße ein.',
      'Achten Sie auf parkende Autos am Straßenrand.',
      'Blinken Sie rechtzeitig links zum Ausscheren und rechts beim Wiedereinordnen.',
      'Warten Sie bei Gegenverkehr!'
    ],
    startPosition: new THREE.Vector3(-85, 0.5, 63.5),
    startRotationY: -Math.PI / 2,
    speedLimit: 30,
    timeLimitSeconds: 150,
    requiredObjectives: [
      'Rechtzeitig vor Engstelle nach links blinken (§ 6 StVO)',
      'Seitenabstand zu parkenden Fahrzeugen einhalten (mind. 1,0 m)',
      'Gegenverkehr Vorrang gewähren',
      'Nach dem Hindernis wieder rechts einordnen und rechts blinken',
    ]
  }
];
